import fetcher, { XCsrfHeaderName } from '@app/utils/fetcher';
import CheckoutBreadcrumbs from '@features/purchasing/components/CheckoutBreadcrumbs';
import ToCheckout from '@features/purchasing/my-basket/ToCheckout';
import type { ICheckoutStep } from '@features/purchasing/types';
import Loader from '@store/components/Loader';
import { emit } from '@store/events';
import useConfiguration from '@store/hooks/useConfiguration';
import ky from 'ky';
import useSWR from 'swr';

import Buttons from './Buttons';
import Lines from './Lines';
import Totals from './Totals';
import type { IGetResponse, ILinks, OnUpdateLine } from './types';

export interface MyBasketProps {
  basketId: string;
  title: string;
  basketName: string;
  notVatRegistered: boolean;
  steps: ICheckoutStep[];
  nextStepUrl: string;
  links?: ILinks;
  apiUrls: { get: string; setLine: string };
}

export default function Index(props: MyBasketProps) {
  const { xsrf } = useConfiguration();
  const { data, error, mutate } = useSWR(props.apiUrls.get, url => fetcher<IGetResponse>(url), {
    revalidateOnFocus: false,
    errorRetryCount: 0,
  });

  const onUpdateLine: OnUpdateLine = async ({ productId, optionId, unitCount }) => {
    const { basketId } = props;

    const fd = new FormData();
    fd.set('basketId', basketId);
    fd.set('productId', productId);
    fd.set('unitCount', `${unitCount}`);
    if (optionId) fd.set('optionId', optionId);

    const response = await ky.put(props.apiUrls.setLine, {
      body: fd,
      headers: { [XCsrfHeaderName]: xsrf },
      throwHttpErrors: false,
    });

    if (!response.ok) {
      console.error('Failed to set item: ' + (await response.text()));
      return;
    }

    const json = await response.json<any>();
    await mutate();
    if ('notification' in json) emit.alert(json.notification);
    if ('widgetUrl' in json) emit.basket.reload(json.widgetUrl);
    if ('analyticsEvent' in json) emit.basket.productChanged(json.analyticsEvent);
  };

  if (!data && !error) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader className="text-base text-primary" />
      </div>
    );
  }

  if (error) {
    return <></>;
  }

  return (
    <>
      <div className="flex flex-col mx-auto">
        {props.steps && <CheckoutBreadcrumbs steps={props.steps} />}
        {props.links && (
          <Buttons
            hasLines={(data?.lines?.length ?? 0) > 0}
            {...props.links}
            basketName={props.basketName}
          />
        )}
        <Lines lines={data?.lines ?? []} onUpdate={onUpdateLine} />
        {data && (
          <>
            {props.notVatRegistered && (
              <div className="flex justify-end mt-1">
                <div className="inline-flex px-4 py-2 bg-gray-200 text-sm">
                  <span>
                    Nakupujete <strong>bez DPH</strong>. Finálne ceny uvidíte v{' '}
                    <strong>Zhrnutí</strong>.
                  </span>
                </div>
              </div>
            )}
            <Totals
              summaryBeforeVat={data.summaryBeforeVat}
              summaryAfterVat={data.summaryAfterVat}
              summaryVat={data.summaryVat}
              vatLevels={data.vatLevels}
              customerDiscount={data.customerDiscount}
            />
            <ToCheckout hasLines={data.lines.length > 0} nextStepUrl={props.nextStepUrl} />
          </>
        )}
      </div>
    </>
  );
}
